// EtiquetaDoLivro.js — a etiqueta na quina de cima do card, e a caixinha de
// escolher/criar.
//
// A pessoa cria a etiqueta escrevendo o nome aqui: não existe tela de
// "gerenciar etiquetas", e é de propósito. Quem quer separar "TCC" de "Prova"
// está olhando para o livro, não para uma tela de cadastro — e uma etiqueta
// sem livro nenhum deixa de existir sozinha no servidor
// (EstanteController::limparEtiquetasOrfas).
//
// A caixinha é um popover comum (não um modal): ela pertence a ESTE card, e
// escurecer a tela inteira para escolher um rótulo de duas palavras seria
// desproporcional. Fecha no Esc, no clique fora e ao escolher.
import React, { useEffect, useRef, useState } from "react";

const EtiquetaDoLivro = ({ etiquetaId, etiquetas = [], max = 20, salvando, onEscolher }) => {
  const [aberto, setAberto] = useState(false);
  const [nova, setNova] = useState("");
  const caixa = useRef(null);

  const atual = etiquetas.find((e) => e.id === etiquetaId) || null;

  useEffect(() => {
    if (!aberto) return;
    const aoClicar = (e) => {
      if (caixa.current && !caixa.current.contains(e.target)) setAberto(false);
    };
    const aoTeclar = (e) => { if (e.key === "Escape") setAberto(false); };
    document.addEventListener("mousedown", aoClicar);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicar);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  const escolher = (nome) => {
    setAberto(false);
    setNova("");
    onEscolher(nome);
  };

  const nomeNovo = nova.trim().slice(0, max);
  const jaExiste = etiquetas.some((e) => e.nome.toLowerCase() === nomeNovo.toLowerCase());

  return (
    // `z-10`, e não 20: a barra de busca gruda no topo com `z-20`, e como a
    // grade vem DEPOIS dela no DOM, o empate de camada fazia a etiqueta
    // desenhar por cima da barra ao rolar (print do dono em 11/09/2026).
    <div ref={caixa} className="absolute -top-3 right-3 z-10">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        disabled={salvando}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        title={atual ? `Etiqueta: ${atual.nome}` : "Pôr uma etiqueta neste livro"}
        className={
          "max-w-[9.5rem] truncate rounded-selo border-2 px-2 py-0.5 text-[11px] font-extrabold uppercase " +
          "tracking-wide transition-colors disabled:opacity-60 " +
          (atual
            ? "border-insigne-amber bg-insigne-orange text-paper"
            : "border-dashed border-white/25 bg-insigne-navy-deep text-white/45 hover:border-white/50 hover:text-white/75")
        }
      >
        {atual ? atual.nome : <><i className="bi bi-tag" /> etiqueta</>}
      </button>

      {aberto && (
        <div
          role="dialog"
          aria-label="Etiqueta do livro"
          className="absolute right-0 top-8 w-60 rounded-xl border border-line bg-insigne-navy-deep p-3
                     shadow-[0_16px_40px_rgba(0,0,0,0.55)]"
        >
          {etiquetas.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {etiquetas.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => escolher(e.nome)}
                  className={
                    "max-w-full truncate rounded-controle border px-2.5 py-1 text-xs font-semibold transition-colors " +
                    (e.id === etiquetaId
                      ? "border-insigne-orange bg-insigne-orange/20 text-insigne-orange"
                      : "border-line bg-white/5 text-white/75 hover:bg-white/10")
                  }
                >
                  {e.nome}
                  <span className="ml-1 text-white/35">{e.livros}</span>
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(ev) => {
              ev.preventDefault();
              if (nomeNovo) escolher(nomeNovo);
            }}
            className="flex items-center gap-1.5"
          >
            <input
              value={nova}
              onChange={(ev) => setNova(ev.target.value)}
              maxLength={max}
              placeholder="Nova etiqueta"
              aria-label="Nome da nova etiqueta"
              className="foco-laranja min-w-0 flex-1 rounded-lg border border-line bg-white/5 px-2.5 py-1.5
                         text-sm text-white placeholder:text-white/35 outline-none
                         transition-colors focus:border-insigne-orange/50"
            />
            <button
              type="submit"
              disabled={!nomeNovo}
              className="foco-laranja shrink-0 rounded-lg bg-insigne-orange px-2.5 py-1.5 text-sm font-bold
                         text-paper transition-colors hover:bg-insigne-orange-dark disabled:opacity-40"
            >
              {jaExiste ? "Usar" : "Criar"}
            </button>
          </form>
          <p className="mt-1.5 text-[11px] text-white/35">
            Até {max} caracteres. A mesma etiqueta serve a vários livros.
          </p>

          {atual && (
            <button
              type="button"
              onClick={() => escolher(null)}
              className="mt-2 w-full rounded-lg border border-line bg-white/5 py-1.5 text-xs font-semibold
                         text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <i className="bi bi-x-circle" /> Tirar a etiqueta
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EtiquetaDoLivro;
